import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { useQuery, keepPreviousData } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { incentivePreview } from "@/lib/labor.functions";
import { useScope } from "@/hooks/use-scope";
import { PageHeader } from "@/components/ui/page-header";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

export const Route = createFileRoute("/_authenticated/app/labor/incentives")({ component: IncentivesPage });

function fmtCents(c: number) {
  return `$${(c / 100).toFixed(2)}`;
}

function IncentivesPage() {
  const { tenantId } = useScope();
  const fn = useServerFn(incentivePreview);
  const [days, setDays] = useState(7);
  const [targetUph, setTargetUph] = useState(80);
  const [bonus, setBonus] = useState(5);
  const [cap, setCap] = useState(500);

  const { data, isLoading } = useQuery({
    queryKey: ["labor-incentives", tenantId, days, targetUph, bonus, cap],
    enabled: !!tenantId,
    queryFn: () => fn({ data: { tenantId: tenantId!, days, targetUph, bonusCentsPerUnit: bonus, maxBonusCents: cap * 100 } }),
    placeholderData: keepPreviousData,
  });

  return (
    <>
      <PageHeader title="Incentive Payouts" description="Preview bonus payouts for picks above target UPH." />
      <div className="px-4 sm:px-6 py-4 space-y-4">
        <Card>
          <CardContent className="p-4 grid grid-cols-2 sm:grid-cols-4 gap-3">
            <Field label="Window (days)" value={days} onChange={setDays} min={1} max={180} />
            <Field label="Target UPH" value={targetUph} onChange={setTargetUph} min={1} max={2000} />
            <Field label="Bonus ¢/unit over target" value={bonus} onChange={setBonus} min={0} max={1000} />
            <Field label="Max bonus per user ($)" value={cap} onChange={setCap} min={0} max={10000} />
          </CardContent>
        </Card>

        <div className="flex items-center justify-between">
          <div className="text-sm text-muted-foreground">
            Total payout: <span className="font-semibold text-foreground">{fmtCents(data?.total_bonus_cents ?? 0)}</span>
          </div>
        </div>

        <Card>
          <CardContent className="p-0 overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Operator</TableHead>
                  <TableHead className="text-right">Hours</TableHead>
                  <TableHead className="text-right">Units</TableHead>
                  <TableHead className="text-right">Target</TableHead>
                  <TableHead className="text-right">Surplus</TableHead>
                  <TableHead className="text-right">UPH</TableHead>
                  <TableHead className="text-right">Bonus</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading && <TableRow><TableCell colSpan={7} className="text-center py-8 text-muted-foreground">Loading…</TableCell></TableRow>}
                {!isLoading && (data?.rows.length ?? 0) === 0 && <TableRow><TableCell colSpan={7} className="text-center py-8 text-muted-foreground">No activity in window.</TableCell></TableRow>}
                {(data?.rows ?? []).map((r) => (
                  <TableRow key={r.user_id}>
                    <TableCell className="text-sm">{r.display_name ?? <span className="font-mono text-xs text-muted-foreground">{r.user_id.slice(0, 8)}</span>}</TableCell>
                    <TableCell className="text-right tabular-nums">{r.hours}</TableCell>
                    <TableCell className="text-right tabular-nums">{r.picks_units}</TableCell>
                    <TableCell className="text-right tabular-nums text-muted-foreground">{r.target_units}</TableCell>
                    <TableCell className="text-right tabular-nums">{r.surplus_units}</TableCell>
                    <TableCell className="text-right tabular-nums">{r.units_per_hour}</TableCell>
                    <TableCell className="text-right tabular-nums font-semibold">{fmtCents(r.bonus_cents)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </div>
    </>
  );
}

function Field({ label, value, onChange, min, max }: { label: string; value: number; onChange: (n: number) => void; min: number; max: number }) {
  return (
    <div className="space-y-1">
      <Label className="text-xs text-muted-foreground">{label}</Label>
      <Input
        type="number"
        value={value}
        min={min}
        max={max}
        onChange={(e) => {
          const n = Number(e.target.value);
          if (Number.isFinite(n)) onChange(Math.max(min, Math.min(max, n)));
        }}
      />
    </div>
  );
}